import { AttachFileViewDTO } from '@/types/attachment/AttachFileViewDTO';
import { AttachmentImage } from '@/components/view/AttachmentImage';
import { AttachmentStatus } from '@/types/attachment/AttachmentStatus';
import { CollapseView } from '@/components/common/CollapseView';
import { EnclosureType } from '@/types/enclosure/EnclosureType';
import { EnclosureViewDTO } from '@/types/enclosure/EnclosureViewDTO';
import { Grid, Skeleton, Typography } from '@mui/material';
import { LetterViewDTO } from '@/types/letter/LetterViewDTO';
import { PhotoGroupHeading } from '@/components/letter/PhotoGroupHeading';
import { SectionCountLabel } from '@/components/common/SectionCountLabel';
import { getEnclosureName, isNotBlank } from '@/utils/string-utils';
import CrosswordImage from '@assets/images/crossword-sample.png';
import NewsImage from '@assets/images/news-sample.jpg';
import React, { FC, Fragment, ReactElement } from 'react';

interface Props {
  letter?: LetterViewDTO;
  isLoading?: boolean;
  /**
   * Opens one photo full screen. The whole list is handed over in the order the letter draws it, so
   * the view can be paged along it without closing.
   */
  onAttachFileClick: (photos: AttachFileViewDTO[], index: number) => void;
  onEnclosureClick: (enclosure: EnclosureViewDTO, name: string) => void;
}

export const LetterView: FC<Props> = (props: Props) => {
  const { isLoading, letter, onAttachFileClick, onEnclosureClick } = props;

  const crosswordEnclosures: EnclosureViewDTO[] = (letter?.enclosures || []).filter(
    (enclosure: EnclosureViewDTO): boolean => {
      return EnclosureType.CROSSWORD === enclosure.type;
    },
  );

  const sudokuEnclosures: EnclosureViewDTO[] = (letter?.enclosures || []).filter(
    (enclosure: EnclosureViewDTO): boolean => {
      return EnclosureType.SUDOKU === enclosure.type;
    },
  );

  // Sports news reuses the regular-news stub: show a single "Главные новости" stub when the letter
  // carries any news-type enclosure (NEWS and/or SPORTS_NEWS) — no separate sports stub.
  const hasNews: boolean = (letter?.enclosures || []).some((enclosure: EnclosureViewDTO): boolean => {
    return EnclosureType.NEWS === enclosure.type || EnclosureType.SPORTS_NEWS === enclosure.type;
  });

  const photos: AttachFileViewDTO[] = letter?.attachFiles || [];

  const declinedPhotos: AttachFileViewDTO[] = photos.filter((photo: AttachFileViewDTO): boolean => {
    return photo.status === AttachmentStatus.DECLINED;
  });

  const acceptedPhotos: AttachFileViewDTO[] = photos.filter((photo: AttachFileViewDTO): boolean => {
    return photo.status !== AttachmentStatus.DECLINED;
  });

  // The two groups are told apart by a heading only when the censor actually split them; a letter
  // whose photos all passed keeps the plain row it has always drawn.
  const isCensorshipSplit: boolean = declinedPhotos.length > 0;

  // The order the tiles are drawn in, which is the order the full-screen view pages along.
  const orderedPhotos: AttachFileViewDTO[] = [...acceptedPhotos, ...declinedPhotos];

  /**
   * Names a photo the way the letter labels its tile: a lone photo is simply «Фото», while one of
   * several carries its own index, which stays with it even when an earlier photo was declined.
   *
   * @param photo The photo.
   * @returns The label.
   */
  const getPhotoName = (photo: AttachFileViewDTO): string => {
    return photos.length > 1 ? `Фото ${photo.photoIndex || 1}` : 'Фото';
  };

  /**
   * Draws one photo tile from its stored preview. The full-size bytes are left to the page, which
   * reads them for the single photo the sender opens.
   *
   * @param photo The photo.
   * @returns The tile.
   */
  const renderPhoto = (photo: AttachFileViewDTO): ReactElement => {
    const name: string = getPhotoName(photo);
    return (
      <Grid item={true} key={photo.id}>
        <AttachmentImage
          fileName={name}
          content={photo.thumbnail || photo.content}
          onClick={(): void => onAttachFileClick(orderedPhotos, orderedPhotos.indexOf(photo))}
        />
      </Grid>
    );
  };

  // The stubs of everything else the letter carries. They are tiles like a photo, but they are not
  // photos, so a censorship verdict never heads them and they never count towards one.
  const enclosureTiles: ReactElement[] = [
    ...sudokuEnclosures.map((enclosure: EnclosureViewDTO, index: number): ReactElement => {
      const name = `${getEnclosureName(enclosure.type)}${sudokuEnclosures.length > 1 ? ` ${index + 1}` : ''}`;
      return (
        <Grid item={true} key={enclosure.uuid}>
          <AttachmentImage
            fileName={name}
            content={enclosure.content}
            onClick={(): void => onEnclosureClick(enclosure, name)}
          />
        </Grid>
      );
    }),
    ...(crosswordEnclosures.length > 0
      ? [
          <Grid item={true} key={'crossword'}>
            <AttachmentImage
              fileName={`${getEnclosureName(EnclosureType.CROSSWORD)}${
                crosswordEnclosures.length > 1 ? ` x${crosswordEnclosures.length}` : ''
              }`}
              image={CrosswordImage}
            />
          </Grid>,
        ]
      : []),
    ...(hasNews
      ? [
          <Grid item={true} key={'news'}>
            <AttachmentImage fileName={getEnclosureName(EnclosureType.NEWS)} image={NewsImage} />
          </Grid>,
        ]
      : []),
  ];

  /**
   * Draws one verdict group: the heading naming it and the tiles of the photos it holds.
   *
   * @param declined Whether the group holds the declined photos, which its dot says.
   * @param title The verdict.
   * @param groupPhotos The photos of the group.
   * @returns The group.
   */
  const renderPhotoGroup = (declined: boolean, title: string, groupPhotos: AttachFileViewDTO[]): ReactElement => (
    <Grid item={true}>
      <Grid container={true} direction={'column'} spacing={1}>
        <Grid item={true}>
          <PhotoGroupHeading declined={declined} title={title} count={String(groupPhotos.length)} />
        </Grid>
        <Grid item={true}>
          <Grid container={true} direction={'row'} spacing={2}>
            {groupPhotos.map(renderPhoto)}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );

  return isLoading ? (
    <Fragment>
      <Skeleton width={'100%'} height={'24px'} />
      <Skeleton width={'100%'} height={'24px'} />
      <Skeleton width={'100%'} height={'24px'} />
      <Skeleton width={'60%'} height={'24px'} />
    </Fragment>
  ) : (
    <Grid container={true} direction={'column'} spacing={3}>
      {/* Only the text collapses: a censorship verdict on a photo must not hide behind «Развернуть». */}
      {isNotBlank(letter?.messageText) && (
        <Grid item={true}>
          <CollapseView wrapHeight={320}>
            <Typography
              variant={'body1'}
              sx={{
                whiteSpace: 'pre-line',
                wordBreak: 'break-word',
              }}>
              {letter?.messageText}
            </Typography>
          </CollapseView>
        </Grid>
      )}
      {isCensorshipSplit ? (
        <Fragment>
          <Grid item={true}>
            <SectionCountLabel label={'ФОТОГРАФИИ ПИСЬМА'} count={`${photos.length} фото`} />
          </Grid>
          {acceptedPhotos.length > 0 && renderPhotoGroup(false, 'Доставлено', acceptedPhotos)}
          {renderPhotoGroup(true, 'Не прошли цензуру', declinedPhotos)}
          {enclosureTiles.length > 0 && (
            <Grid item={true}>
              <Grid container={true} direction={'row'} spacing={2}>
                {enclosureTiles}
              </Grid>
            </Grid>
          )}
        </Fragment>
      ) : (
        (acceptedPhotos.length > 0 || enclosureTiles.length > 0) && (
          <Grid item={true}>
            <Grid container={true} direction={'row'} spacing={2}>
              {acceptedPhotos.map(renderPhoto)}
              {enclosureTiles}
            </Grid>
          </Grid>
        )
      )}
    </Grid>
  );
};
